import { get } from "lodash";
import constants from "./constants";
var SOUND_PATHS = {
  qc: "/sounds/QC_tone.mp3",
  ra: "/sounds/RA_tone.mp3"
};

/** Tiny valid WAV — used only to satisfy autoplay policy on first user gesture. */
var SILENT_WAV_DATA_URI = "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
var pooledAudioByUrl = new Map();
var notificationAudioUnlockInstalled = false;
function getPooledAudio(url) {
  var el = pooledAudioByUrl.get(url);
  if (!el) {
    el = new Audio(url);
    el.preload = "auto";
    pooledAudioByUrl.set(url, el);
  }
  return el;
}

/**
 * Browsers block notification audio until there has been a user gesture on the page.
 * Call once on app load; the first pointer/touch/key interaction primes playback so
 * later `play()` calls from polling/websockets can succeed.
 */
export function initNotificationAudioUnlock() {
  if (typeof window === "undefined" || notificationAudioUnlockInstalled) return;
  notificationAudioUnlockInstalled = true;
  var _unlock = function unlock() {
    window.removeEventListener("pointerdown", _unlock, true);
    window.removeEventListener("touchstart", _unlock, true);
    window.removeEventListener("keydown", _unlock, true);

    // `play()` must be invoked synchronously from this handler (not after an await).
    try {
      var silent = new Audio(SILENT_WAV_DATA_URI);
      void silent.play().then(function () {
        silent.pause();
      }, function () {
        /* still blocked or data URI unsupported */
      });
    } catch (_unused) {
      /* ignore */
    }
    try {
      var Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        var ctx = new Ctx();
        void ctx.resume().then(function () {
          void ctx.close();
        }, function () {
          /* ignore */
        });
      }
    } catch (_unused2) {
      /* ignore */
    }
    var _loop = function _loop() {
      var url = _Object$values[_i];
      try {
        var el = getPooledAudio(url);
        var prevVolume = el.volume;
        el.volume = 0.0001;
        void el.play().then(function () {
          el.pause();
          el.currentTime = 0;
          el.volume = prevVolume;
        }, function () {
          el.volume = prevVolume;
        });
      } catch (_unused3) {
        /* ignore */
      }
    };
    for (var _i = 0, _Object$values = Object.values(SOUND_PATHS); _i < _Object$values.length; _i++) {
      _loop();
    }
  };
  window.addEventListener("pointerdown", _unlock, true);
  window.addEventListener("touchstart", _unlock, true);
  window.addEventListener("keydown", _unlock, true);
}

/** Subset of auth profile used for QC vs RA tone; full `UserInfo` is accepted. */

function normalizeRole(role) {
  return String(role !== null && role !== void 0 ? role : "").toLowerCase().trim();
}
var RESEARCH_ASSISTANT_ROLES = [constants.POCUS_USER_ROLES.POCUS_RESEARCH_ASSISTANT, constants.POCUS_USER_ROLES.POCUS_RESEARCH_ASSISTANT_AND_SONOGRAPHER];
function roleLooksLikeResearchAssistant(normalized) {
  if (!normalized) return false;
  if (RESEARCH_ASSISTANT_ROLES.includes(normalized)) return true;
  return normalized.includes("research_assistant");
}

/** Matches `isQcRadiologistRoleName` (role string contains "qc", case-insensitive). */
function roleContainsQc(roleName) {
  var raw = String(roleName !== null && roleName !== void 0 ? roleName : "").toLowerCase().trim();
  return !!raw && raw.includes("qc");
}
function isQcRoleString(role) {
  var n = normalizeRole(role);
  return n === constants.POCUS_USER_ROLES.POCUS_QC_RADIOLOGIST || roleContainsQc(role);
}
function isRaRoleString(role) {
  return roleLooksLikeResearchAssistant(normalizeRole(role));
}

/**
 * Resolves QC vs RA notification tone.
 * @param extraWorkspaceRoles — roles from `/workspaces/permitted/` (profile `accessible_sources` often has no `role`).
 */
export function resolveNotificationSoundKind(userInfo, extraWorkspaceRoles) {
  var roleName = get(userInfo, ["details", "name"], "");
  var designation = get(userInfo, ["details", "designation"], "");
  var rawSources = get(userInfo, ["accessible_sources"], []);
  var sources = Array.isArray(rawSources) ? rawSources : [];
  var qcFromProfile = isQcRoleString(roleName) || isQcRoleString(designation);
  var qcFromSources = sources.some(function (s) {
    var role = get(s, "role");
    return isQcRoleString(role);
  });
  var qcFromWorkspaces = (extraWorkspaceRoles !== null && extraWorkspaceRoles !== void 0 ? extraWorkspaceRoles : []).some(function (r) {
    return isQcRoleString(r);
  });
  var isQc = qcFromProfile || qcFromSources || qcFromWorkspaces;
  var raFromProfile = isRaRoleString(roleName) || isRaRoleString(designation);
  var raFromSources = sources.some(function (s) {
    return isRaRoleString(get(s, "role"));
  });
  var raFromWorkspaces = (extraWorkspaceRoles !== null && extraWorkspaceRoles !== void 0 ? extraWorkspaceRoles : []).some(function (r) {
    return isRaRoleString(r);
  });
  var isRa = raFromProfile || raFromSources || raFromWorkspaces;
  if (isQc) return "qc";
  if (isRa) return "ra";
  return "default";
}
function playMp3Url(url) {
  if (typeof window === "undefined") return;
  try {
    var audio = getPooledAudio(url);
    audio.currentTime = 0;
    void audio.play()["catch"](function () {
      // Autoplay policy (no prior gesture), decode error, or missing asset
    });
  } catch (_unused4) {
    // Ignore
  }
}

/** Unread + `immediate_action.ultrasound_reupload` snapshot for RA tone eligibility. */

/**
 * RA_tone when the unread bump is entirely from `ultrasound_reupload` (no other buckets).
 */
export function isPureUltrasoundReuploadUnreadIncrease(ctx) {
  var totalDelta = ctx.currentUnreadTotal - ctx.previousUnreadTotal;
  var reuploadDelta = ctx.currentUltrasoundReuploadCount - ctx.previousUltrasoundReuploadCount;
  return totalDelta > 0 && reuploadDelta === totalDelta && reuploadDelta > 0;
}

/**
 * Unread went up from both `ultrasound_reupload` and at least one other notification type.
 */
export function isMixedUltrasoundReuploadAndOtherUnreadIncrease(ctx) {
  var totalDelta = ctx.currentUnreadTotal - ctx.previousUnreadTotal;
  var reuploadDelta = ctx.currentUltrasoundReuploadCount - ctx.previousUltrasoundReuploadCount;
  return totalDelta > 0 && reuploadDelta > 0 && reuploadDelta < totalDelta;
}
function shouldPlayRaToneMp3ForUnreadIncrease(ctx) {
  return isPureUltrasoundReuploadUnreadIncrease(ctx) || isMixedUltrasoundReuploadAndOtherUnreadIncrease(ctx);
}

/** Legacy short beep when role does not map to QC/RA tones. */
function playDefaultBeep() {
  if (typeof window === "undefined") return;
  try {
    var ctx = new (window.AudioContext || window.webkitAudioContext)();
    var gain = ctx.createGain();
    var osc = ctx.createOscillator();
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.type = "sine";
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.2);
  } catch (_unused5) {
    // Ignore if AudioContext is not allowed (e.g. no user gesture yet)
  }
}

/**
 * Plays a notification sound when unread count increases.
 * QC: QC_tone.mp3 on any increase (unchanged).
 * RA: RA_tone.mp3 when the bump includes `ultrasound_reupload` (pure-only or mixed with other types);
 *     if unread increases with no reupload component → beep.
 * If both QC and RA apply, QC wins.
 * @param extraWorkspaceRoles — from permitted-workspaces API when profile sources omit `role`.
 * @param unreadContext — used for RA to detect pure vs mixed vs non-reupload bumps.
 */
export function playNotificationSound(userInfo, extraWorkspaceRoles, unreadContext) {
  var kind = resolveNotificationSoundKind(userInfo, extraWorkspaceRoles);
  if (kind === "default") {
    playDefaultBeep();
    return;
  }
  if (kind === "qc") {
    playMp3Url(SOUND_PATHS.qc);
    return;
  }
  // RA
  if (unreadContext && shouldPlayRaToneMp3ForUnreadIncrease(unreadContext)) {
    playMp3Url(SOUND_PATHS.ra);
    return;
  }
  playDefaultBeep();
}